import { ROOT, type WizardFieldErrors } from "@/base";
import { ApiError } from "@/lib/api";
import type {
  FieldSection,
  ManifestField,
  ProductManifest,
} from "@/lib/brokerProducts";
import { numberBox, type BoxValue } from "@/lib/displayFormat";
import {
  classCodeName,
  incompleteCarriersOf,
  missingFieldsOf,
  readIndicationResults,
  wageTagText,
  type BrokerQuote,
  type CarrierIndication,
  type CodeOption,
  type MissingField,
} from "@/lib/submissions/brokerSubmissions";
import { numericKind, type WcAnswer } from "@/lib/submissions/wc/questions";
import { incompleteAsFieldErrors } from "@/lib/submissions/wc/carrierStep";

// Submit's pure rules (BRK-S305): which page asks for a field PMS says is
// missing, and the checklist that carries those fields to the last step. And
// what a save says about the quotes (BRK-S307).

export const WC_BASE_PATH = "/submissions/wc";

/** The steps that ask the broker something, in the wizard's order: each a
 *  card in Preview submission. */
export const WC_STEP_IDS = [
  "business",
  "locations",
  "people",
  "questions",
] as const;
export type WcStepId = (typeof WC_STEP_IDS)[number];

/** Each step's title: the stepper's, and Preview submission's cards'. */
export const WC_STEP_TITLES: Readonly<Record<WcStepId, string>> = {
  business: "Business",
  locations: "Locations",
  people: "People",
  questions: "Questions and coverage",
};

/** The last step (BRK-S387): what each carrier still asks, then Submit. */
export const WC_LAST_STEP = "carrier-questions";
export const WC_LAST_STEP_TITLE = "Carrier-specific questions";

/** The pages that ask the broker something, in the wizard's order
 *  (BRK-S387): a step made of sub-steps has one per sub-step, at
 *  `step/substep`. */
export const WC_PAGES = [
  "business/basic",
  "business/history",
  "locations",
  "people",
  "questions/coverage",
  "questions/general",
  "questions/schedule-rating",
] as const;
export type WcPage = (typeof WC_PAGES)[number];

/** Each page's title: its heading, and the name a missing field's link
 *  gives its page. */
export const WC_PAGE_TITLES: Readonly<Record<WcPage, string>> = {
  "business/basic": "Basic information",
  "business/history": "Characteristics & History",
  locations: "Locations",
  people: "People",
  "questions/coverage": "Coverage",
  "questions/general": "General information (ACORD 130)",
  "questions/schedule-rating": "Schedule rating",
};

/** A page of a saved application: a step, which opens at its first
 *  unfinished sub-step, a sub-step, or the last step. */
export function wcStepPath(
  id: string,
  step: WcStepId | WcPage | typeof WC_LAST_STEP,
): string {
  return `${WC_BASE_PATH}/${encodeURIComponent(id)}/${step}`;
}

// The keys each page writes (draft.ts: saveWcBusiness, saveWcLocations and
// saveWcPeople), as PMS names them. A manifest question's page is its
// declared section's (PMS-S703).
const WRITTEN_BY: Readonly<Record<string, WcPage>> = {
  legalName: "business/basic",
  state: "business/basic",
  ein: "business/basic",
  entityType: "business/basic",
  emailAddress: "business/basic",
  phones: "business/basic",
  businessStartDate: "business/basic",
  effectiveDate: "business/basic",
  mailingAddress: "locations",
  locations: "locations",
  exposures: "locations",
  includeOfficers: "people",
  personnel: "people",
  // At least one owner or officer (PMS-S813): a floor key, not a field.
  owners: "people",
  // The prior-coverage rows (PMS-S810): a floor key, not a manifest field.
  priorCoverage: "business/history",
};

/** Each manifest section's page (BRK-S387). Keyed by the pinned contract's
 *  sections, so a section a re-pin adds fails to compile here until it has a
 *  page; a section PMS sends ahead of the pin asks on no page. */
const SECTION_PAGE: Readonly<Record<FieldSection, WcPage>> = {
  wc_basic_information: "business/basic",
  wc_history: "business/history",
  wc_coverage: "questions/coverage",
  wc_general_information: "questions/general",
  wc_schedule_rating: "questions/schedule-rating",
};

/**
 * The page that asks for a field, or null when none does. A nested key
 * (`exposures.locationId.county`) belongs to the field it sits in. A manifest
 * question is its section's page, never placed by its name (D-10 rule 1).
 */
export function stepOwning(
  field: Pick<MissingField, "key">,
  manifest: ProductManifest | null,
): WcPage | null {
  const [root] = field.key.split(".");
  if (Object.prototype.hasOwnProperty.call(WRITTEN_BY, root)) {
    return WRITTEN_BY[root];
  }
  const section = manifest?.fields.find((f) => f.key === root)?.section;
  return section && Object.prototype.hasOwnProperty.call(SECTION_PAGE, section)
    ? SECTION_PAGE[section]
    : null;
}

/** The editable home of a missing answer, including an existing insured’s FEIN. */
export function missingFieldPath(
  field: Pick<MissingField, "key">,
  manifest: ProductManifest | null,
  id: string,
  existingAccountId?: string | null,
): string | null {
  if (field.key === "ein" && existingAccountId)
    return `/accounts/${encodeURIComponent(existingAccountId)}`;
  const step = stepOwning(field, manifest);
  return step ? wcStepPath(id, step) : null;
}

/** One field the application still needs, with the page that asks for it. */
export interface MissingItem {
  key: string;
  label: string;
  step: WcPage | null;
}

/** PMS's missing fields, each under its page, in the wizard's order; those no
 *  page asks for come last. PMS's own order holds within a page. */
export function missingChecklist(
  missing: readonly MissingField[],
  manifest: ProductManifest | null,
): MissingItem[] {
  const rank = (step: WcPage | null) =>
    step === null ? WC_PAGES.length : WC_PAGES.indexOf(step);
  return missing
    .map((f) => ({ key: f.key, label: f.label, step: stepOwning(f, manifest) }))
    .sort((a, b) => rank(a.step) - rank(b.step));
}

// The wizard has one channel from a refused Submit to the page on screen: its
// field errors. The checklist rides it as `missing.<page>.<key>` → label, with
// `other` for a field no page asks for.
const MISSING = "missing.";
const NO_STEP = "other";

function isPage(value: string): value is WcPage {
  return (WC_PAGES as readonly string[]).includes(value);
}

export function missingAsFieldErrors(
  items: readonly MissingItem[],
): Record<string, string> {
  return Object.fromEntries(
    items.map((item) => [
      `${MISSING}${item.step ?? NO_STEP}.${item.key}`,
      item.label,
    ]),
  );
}

export function missingFromFieldErrors(
  errors: WizardFieldErrors,
): MissingItem[] {
  return Object.entries(errors).flatMap(([path, label]) => {
    if (!path.startsWith(MISSING)) return [];
    const rest = path.slice(MISSING.length);
    const dot = rest.indexOf(".");
    if (dot <= 0 || dot === rest.length - 1) return [];
    const step = rest.slice(0, dot);
    return [
      { key: rest.slice(dot + 1), label, step: isPage(step) ? step : null },
    ];
  });
}

export const SUBMIT_MISSING =
  "This application needs a few more answers before it can be submitted.";

/** A refused Submit, ready for the wizard: PMS's missing fields (a 400 with
 *  `details.missingFields`, PMS-S708) become the last step's checklist, a
 *  carrier with questions left (a 409 `carrier_questions_incomplete`,
 *  PMS-S815) lands on that carrier with PMS's own sentence, and any other
 *  refusal is left as it was. */
export function submitRefusal(
  err: unknown,
  manifest: ProductManifest | null,
): unknown {
  const carriers = incompleteCarriersOf(err);
  if (carriers !== null && err instanceof ApiError) {
    return new ApiError(400, err.message, {
      error: err.message,
      details: {
        fieldErrors: {
          [ROOT]: err.message,
          ...incompleteAsFieldErrors(carriers),
        },
      },
    });
  }
  const missing = missingFieldsOf(err);
  if (missing === null) return err;
  return new ApiError(400, SUBMIT_MISSING, {
    error: SUBMIT_MISSING,
    details: {
      fieldErrors: {
        [ROOT]: SUBMIT_MISSING,
        ...missingAsFieldErrors(missingChecklist(missing, manifest)),
      },
    },
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// ─── What a save says about the quotes (BRK-S307) ───────────────────────────

export const QUOTES_OUTDATED =
  "Your quotes will need updating, because the application changed.";

/** What the wizard says once a save has gone through: that the quotes need
 *  updating, only when PMS marked one outdated (PMS-S707, read as the
 *  comparison reads it, BRK-S341). It never guesses from the edit: with no
 *  quotes, or every one current, it says nothing. */
export function quotesNotice(quotes: readonly BrokerQuote[]): string | null {
  return quotes.some((q) => q.outdated === true) ? QUOTES_OUTDATED : null;
}

// ─── How Preview submission shows what was entered ──────────────────────────

/** A question's answer as its field's box shows it (BRK-S349): Yes or No, a
 *  number as its control shows it (D-10: money grouped, with `$`; the % of
 *  work subcontracted with `%`), an option by its label. */
export function answerBox(field: ManifestField, value: WcAnswer): BoxValue {
  if (value === undefined) return { value: undefined };
  if (typeof value === "boolean") return { value: value ? "Yes" : "No" };
  if (typeof value === "number") {
    const box = numberBox(numericKind(field) ?? "number", value);
    return field.unit ? { ...box, end: field.unit } : box;
  }
  if (field.type === "select") {
    return {
      value:
        field.options?.find((o) => o.value === value)?.label ?? String(value),
    };
  }
  return { value: String(value) };
}

/** A payroll row's class code as its field shows it once chosen: "5403 —
 *  Carpentry". Undefined while the row has none. */
export function classCodeText(code: CodeOption | null): string | undefined {
  if (!code) return undefined;
  const name = classCodeName(code);
  const text = name ? `${code.code} — ${name}` : code.code;
  // A dual-wage code says which of its pair it is (BRK-S394).
  const wage = wageTagText(code);
  return wage ? `${text} · ${wage}` : text;
}

// ─── What Submit hands the submission's page ────────────────────────────────

/** A carrier whose quote Submit had PMS record a request for (PMS-S815). */
export interface RequestedCarrier {
  carrierId: string;
  carrierName: string;
}

/** The router state Submit lands with: the fan-out the last step ran, so the
 *  comparison shows it without asking again, and the carriers whose quote
 *  the page requests, all at once (BRK-S393). */
export interface SubmittedState {
  indications?: unknown;
  requested: RequestedCarrier[];
}

/** The carriers Submit handed over, read tolerantly; none when nothing was
 *  handed. */
export function handedRequests(state: unknown): RequestedCarrier[] {
  if (!isRecord(state) || !Array.isArray(state.requested)) return [];
  return state.requested.flatMap((c: unknown): RequestedCarrier[] =>
    isRecord(c) &&
    typeof c.carrierId === "string" &&
    typeof c.carrierName === "string"
      ? [{ carrierId: c.carrierId, carrierName: c.carrierName }]
      : [],
  );
}

/** The fan-out Submit handed over, read tolerantly; null when nothing was
 *  handed (any other way onto the page, or a check that failed). */
export function handedIndications(state: unknown): CarrierIndication[] | null {
  if (!isRecord(state) || !("indications" in state)) return null;
  return readIndicationResults(state.indications);
}
